import axios, { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { API_ENDPOINT } from '../utils/constants';
import { APIError, PaginationData } from '../utils/types';

export interface WhitelistedDomain {
  id: number;
  domain: string;
  description: string | null;
  is_active: boolean;
  client_name: string | null;
  email_provider: number | null;
  email_sender: string | null;
  support_email: string | null;
  client_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface CreateDomainPayload {
  domain: string;
  description?: string;
  is_active?: boolean;
  client_name?: string;
  email_provider?: number;
  email_sender?: string;
  support_email?: string;
  client_id?: string;
}

interface UpdateDomainPayload {
  domain?: string;
  description?: string;
  is_active?: boolean;
  client_name?: string;
  email_provider?: number;
  email_sender?: string;
  support_email?: string;
  client_id?: string;
}

interface APIResponse<T> {
  success: boolean;
  status: number;
  error: Array<{ form_level_error?: string }>;
  data: T | null;
}

interface DomainsListResponse {
  pagination_data: PaginationData & {
    total_count: number;
    total_pages: number;
  };
  domains: WhitelistedDomain[];
}

interface DomainResponse {
  domain: WhitelistedDomain;
}

interface MessageResponse {
  message: string;
  domain_id?: number;
}

interface FetchDomainsParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean | string;
  include_deleted?: boolean;
}

const useWhitelistedDomains = (params?: FetchDomainsParams) => {
  const queryClient = useQueryClient();
  const { page = 1, limit = 20, search = '', is_active, include_deleted = false } = params || {};

  const fetchDomains = async (): Promise<DomainsListResponse> => {
    try {
      const queryParams: Record<string, string | number | boolean> = {
        page,
        limit,
      };

      if (search) {
        queryParams.search = search;
      }

      if (is_active !== undefined && is_active !== '') {
        queryParams.is_active = String(is_active === true || is_active === 'true');
      }

      if (include_deleted) {
        queryParams.include_deleted = true;
      }

      const {
        data: { data },
      } = await axios.get<APIResponse<DomainsListResponse>>(
        `${API_ENDPOINT}/whitelist-domains`,
        { params: queryParams }
      );

      if (!data) {
        return {
          pagination_data: {
            page_no: 1,
            page_size: limit,
            page_next: false,
            page_prev: false,
            total_count: 0,
            total_pages: 0,
          },
          domains: [],
        };
      }

      return data;
    } catch (error: any) {
      throw error?.response?.data || error;
    }
  };

  const createDomain = async (
    payload: CreateDomainPayload
  ): Promise<MessageResponse> => {
    try {
      const { data } = await axios.post<APIResponse<MessageResponse>>(
        `${API_ENDPOINT}/whitelist-domains`,
        payload
      );

      if (!data.success || !data.data) {
        throw new Error(
          data.error[0]?.form_level_error || 'Failed to create domain'
        );
      }

      return data.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.[0]?.form_level_error ||
        error?.message ||
        'Failed to create domain';
      throw new Error(errorMessage);
    }
  };

  const updateDomain = async (
    id: number,
    payload: UpdateDomainPayload
  ): Promise<MessageResponse> => {
    try {
      const { data } = await axios.put<APIResponse<MessageResponse>>(
        `${API_ENDPOINT}/whitelist-domains/${id}`,
        payload
      );

      if (!data.success || !data.data) {
        throw new Error(
          data.error[0]?.form_level_error || 'Failed to update domain'
        );
      }

      return data.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.[0]?.form_level_error ||
        error?.message ||
        'Failed to update domain';
      throw new Error(errorMessage);
    }
  };

  const deleteDomain = async (
    id: number,
    permanent: boolean = false
  ): Promise<MessageResponse> => {
    try {
      const { data } = await axios.delete<APIResponse<MessageResponse>>(
        `${API_ENDPOINT}/whitelist-domains/${id}`,
        {
          params: { permanent },
        }
      );

      if (!data.success || !data.data) {
        throw new Error(
          data.error[0]?.form_level_error || 'Failed to delete domain'
        );
      }

      return data.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.[0]?.form_level_error ||
        error?.message ||
        'Failed to delete domain';
      throw new Error(errorMessage);
    }
  };

  const getDomainById = async (id: number): Promise<WhitelistedDomain> => {
    try {
      const {
        data: { data },
      } = await axios.get<APIResponse<DomainResponse>>(
        `${API_ENDPOINT}/whitelist-domains/${id}`
      );

      if (!data || !('domain' in data)) {
        throw new Error('Domain not found');
      }

      return data.domain;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.[0]?.form_level_error ||
        error?.message ||
        'Failed to fetch domain';
      throw new Error(errorMessage);
    }
  };

  const toggleStatus = async (
    id: number,
    is_active: boolean
  ): Promise<MessageResponse> => {
    return updateDomain(id, { is_active });
  };

  interface CSVImportResponse {
    created: number;
    skipped: number;
    errors: number;
    created_domains: Array<{ row: number; domain: string }>;
    skipped_domains: Array<{ row: number; domain: string; reason: string }>;
    error_details: Array<{ row: number; domain: string; error: string }>;
  }

  const importFromCSV = async (file: File): Promise<CSVImportResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post<APIResponse<CSVImportResponse>>(
        `${API_ENDPOINT}/whitelist-domains/import-csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!data.success || !data.data) {
        throw new Error(
          data.error[0]?.form_level_error || 'Failed to import domains from CSV'
        );
      }

      return data.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.[0]?.form_level_error ||
        error?.message ||
        'Failed to import domains from CSV';
      throw new Error(errorMessage);
    }
  };

  const query = useQuery<
    DomainsListResponse,
    AxiosResponse<APIError>
  >(
    ['whitelisted_domains', page, limit, search, is_active, include_deleted],
    fetchDomains,
    {
      retry: false,
    }
  );

  const createMutation = useMutation(createDomain, {
    onSuccess: () => {
      queryClient.invalidateQueries(['whitelisted_domains']);
    },
  });

  const updateMutation = useMutation(
    ({ id, payload }: { id: number; payload: UpdateDomainPayload }) =>
      updateDomain(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whitelisted_domains']);
      },
    }
  );

  const deleteMutation = useMutation(
    ({ id, permanent }: { id: number; permanent: boolean }) =>
      deleteDomain(id, permanent),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whitelisted_domains']);
      },
    }
  );

  const toggleStatusMutation = useMutation(
    ({ id, is_active }: { id: number; is_active: boolean }) =>
      toggleStatus(id, is_active),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whitelisted_domains']);
      },
    }
  );

  const importCSVMutation = useMutation(importFromCSV, {
    onSuccess: () => {
      queryClient.invalidateQueries(['whitelisted_domains']);
    },
  });

  const deleteDomainWrapper = async (
    id: number,
    permanent: boolean = false
  ): Promise<MessageResponse> => {
    return deleteMutation.mutateAsync({ id, permanent });
  };

  return {
    ...query,
    createDomain: createMutation.mutateAsync,
    updateDomain: updateMutation.mutateAsync,
    deleteDomain: deleteDomainWrapper,
    toggleStatus: toggleStatusMutation.mutateAsync,
    importFromCSV: importCSVMutation.mutateAsync,
    getDomainById,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isTogglingStatus: toggleStatusMutation.isLoading,
    isImportingCSV: importCSVMutation.isLoading,
  };
};

export default useWhitelistedDomains;
